// src/pages/payouts/Payouts.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container, Row, Col, Table, Button, Modal, Form,
  Spinner, Alert, InputGroup, FormControl, Pagination, Dropdown,
} from "react-bootstrap";
import { useSearchParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../../services/api";
import { formatCurrency } from "../../utils/format";
import { Colors } from "../../constants/Colors";

const MySwal = withReactContent(Swal);
const PER_PAGE = 15;

const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover: "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

const STATUS_META = {
  succeeded: { color: Colors.SUCCESS, bg: `${Colors.SUCCESS}1f`, label: "Succeeded" },
  processing: { color: Colors.WARNING, bg: `${Colors.WARNING}1f`, label: "Processing" },
  pending: { color: Colors.WARNING, bg: `${Colors.WARNING}1f`, label: "Pending" },
  failed: { color: Colors.RED, bg: `${Colors.RED}1f`, label: "Failed" },
  cancelled: { color: Colors.GRAY, bg: `${Colors.GRAY}26`, label: "Cancelled" },
};

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "succeeded", label: "Succeeded" },
  { value: "failed", label: "Failed" },
];

/* ---------- Common BIC codes for the admin's convenience ---------- */
const COMMON_BICS = [
  { code: "GXCHPHM2", label: "GCash" },
  { code: "BNORPHMM", label: "BDO" },
  { code: "BOPIPHMM", label: "BPI" },
  { code: "MBTCPHMM", label: "Metrobank" },
  { code: "TLBPPHMM", label: "Landbank" },
  { code: "UBPHPHMM", label: "UnionBank" },
  { code: "SECB", label: "Security Bank" },
  { code: "CHBKPHMM", label: "China Bank" },
];

const EMPTY_PAYOUT_FORM = {
  amount: "",
  method: "gcash",
  account_name: "",
  account_number: "",
  bank_code: "",
  bic: "",
};

/* ================================================================== */
/* HELPERS                                                            */
/* ================================================================== */

const getStatusMeta = (status) =>
  STATUS_META[(status || "").toLowerCase()] || STATUS_META.pending;

const getInitials = (operator, fallbackId) => {
  if (operator) {
    const first = operator.first_name?.[0] || "";
    const last = operator.last_name?.[0] || "";
    return `${first}${last}`.toUpperCase() || "?";
  }
  return fallbackId ? `#${fallbackId}` : "?";
};

const getOperatorName = (p) =>
  p.operator
    ? `${p.operator.first_name} ${p.operator.last_name}`
    : `Operator #${p.operator_id}`;

const formatDateTime = (v) => {
  if (!v) return "—";
  return new Date(v).toLocaleString("en-PH", {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
};

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

const Styles = () => (
  <style>{`
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }

    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }

    .bk-stat { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 16px 18px; display: flex; align-items: center; gap: 12px; transition: transform .18s ease, box-shadow .18s ease; height: 100%; }
    .bk-stat:hover { transform: translateY(-2px); box-shadow: ${T.shadowHover}; }
    .bk-stat-icon { width: 44px; height: 44px; border-radius: 11px; display: grid; place-items: center; font-size: 19px; flex-shrink: 0; }
    .bk-stat-val { font-size: 21px; font-weight: 700; line-height: 1; color: ${Colors.TEXT_DARK}; }
    .bk-stat-lbl { font-size: 11px; color: ${Colors.TEXT_LIGHT}; text-transform: uppercase; letter-spacing: .05em; margin-top: 4px; font-weight: 600; }
    .bk-stat-val.text { font-size: 15px; }

    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-header { padding: 16px 22px; border-bottom: ${T.border}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; background: #fff; }
    .bk-card-header h5 { margin: 0; font-weight: 600; color: ${Colors.TEXT_DARK}; font-size: 15.5px; display: flex; align-items: center; gap: 8px; }
    .bk-card-header small { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }

    .bk-toolbar { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 12px; box-shadow: ${T.shadow}; margin-bottom: 18px; }
    .bk-filter-btn { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; font-weight: 500; text-align: left; display: flex; align-items: center; justify-content: space-between; height: auto !important; min-height: 42px; }
    .bk-filter-btn:hover, .bk-filter-btn:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: none !important; color: ${Colors.TEXT_DARK} !important; }
    .bk-filter-btn::after { margin-left: auto; }

    .bk-table { margin: 0; }
    .bk-table thead th { background: ${Colors.LIGHT_GRAY}; border: none; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; font-weight: 700; padding: 13px 18px; white-space: nowrap; }
    .bk-table tbody td { padding: 15px 18px; border-color: ${Colors.BORDER_GRAY}; vertical-align: middle; font-size: 13.5px; color: ${Colors.TEXT_DARK}; }
    .bk-table tbody tr { transition: background .15s; }
    .bk-table tbody tr:hover { background: ${Colors.LIGHT_GRAY}55; }

    .bk-avatar { width: 34px; height: 34px; border-radius: 10px; background: linear-gradient(135deg, ${Colors.GRADIENT_START}, ${Colors.GRADIENT_END}); color: #fff; font-weight: 600; font-size: 13px; display: grid; place-items: center; flex-shrink: 0; }
    .bk-id-chip { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 11px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_LIGHT}; padding: 3px 7px; border-radius: 5px; }

    .bk-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; line-height: 1.4; }
    .bk-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
    .bk-method-pill { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
    .bk-method-pill.gcash { color: ${Colors.INFO}; background: ${Colors.INFO}1f; }
    .bk-method-pill.bank { color: ${Colors.SECONDARY_BLUE}; background: ${Colors.SECONDARY_BLUE}1f; }
    .bk-method-pill.unknown { color: ${Colors.GRAY}; background: ${Colors.GRAY}26; }

    .bk-empty { text-align: center; padding: 60px 20px; }
    .bk-empty-icon { width: 80px; height: 80px; margin: 0 auto 18px; border-radius: 50%; background: ${Colors.LIGHT_GRAY}; display: grid; place-items: center; font-size: 34px; color: ${Colors.GRAY}; }
    .bk-empty h5 { font-weight: 600; margin-bottom: 6px; }

    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    .bk-btn-primary { background: ${Colors.PRIMARY} !important; border: none !important; color: #fff !important; border-radius: ${T.radiusSm} !important; font-weight: 600; padding: 8px 16px; font-size: 13.5px; transition: all .18s; }
    .bk-btn-primary:hover { background: #e87a2e !important; transform: translateY(-1px); box-shadow: 0 6px 16px ${Colors.PRIMARY}44; }
    .bk-btn-primary:disabled { opacity: .6 !important; cursor: not-allowed !important; transform: none !important; box-shadow: none !important; }
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-ghost:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }
    .bk-btn-ghost:disabled { opacity: .6 !important; cursor: not-allowed !important; }

    .bk-section { background: ${Colors.LIGHT_GRAY}; border-radius: ${T.radiusSm}; padding: 16px; margin-bottom: 14px; }
    .bk-section-title { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; margin-bottom: 12px; display: flex; align-items: center; gap: 7px; }
    .bk-section-title i { color: ${Colors.PRIMARY}; font-size: 14px; }

    .modal-content { border-radius: ${T.radius}px; border: none; box-shadow: 0 24px 60px rgba(16,24,40,.18); }
    .modal-header { border-bottom: ${T.border}; padding: 18px 22px; }
    .modal-title { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 16px; }
    .modal-body { padding: 20px 22px; }
    .modal-footer { border-top: ${T.border}; padding: 14px 22px; }

    .page-item.active .page-link { background: ${Colors.PRIMARY} !important; border-color: ${Colors.PRIMARY} !important; color: #fff !important; }
    .page-link { color: ${Colors.TEXT_DARK} !important; border-color: ${Colors.BORDER_GRAY} !important; }
    .page-link:hover { background: ${Colors.LIGHT_GRAY} !important; }

    .dropdown-menu { border-radius: ${T.radiusSm}; border: ${T.border}; box-shadow: ${T.shadow}; padding: 6px; }
    .dropdown-item { border-radius: 6px; padding: 8px 12px; font-size: 13px; }
    .dropdown-item.active { background: ${Colors.PRIMARY}1a !important; color: ${Colors.TEXT_DARK} !important; }

    .spin { animation: bk-spin 1s linear infinite; display: inline-block; }
    @keyframes bk-spin { to { transform: rotate(360deg); } }
  `}</style>
);

/* ================================================================== */
/* PRESENTATIONAL                                                     */
/* ================================================================== */

const StatusPill = ({ status }) => {
  const m = getStatusMeta(status);
  return (
    <span className="bk-pill" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

const MethodPill = ({ method }) => {
  const key = (method || "").toLowerCase();
  const cls = key === "gcash" ? "gcash" : key === "bank" ? "bank" : "unknown";
  return (
    <span className={`bk-method-pill ${cls}`}>
      {(method || "—").toUpperCase()}
    </span>
  );
};

/* ================================================================== */
/* MAIN COMPONENT                                                     */
/* ================================================================== */

const Payouts = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const autoOpenedRef = useRef(false);

  /* ---------------- List state ---------------- */
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });

  /* ---------------- Payout dialog ---------------- */
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [payoutForm, setPayoutForm] = useState(EMPTY_PAYOUT_FORM);
  const [submitting, setSubmitting] = useState(false);

  /* ================================================================ */
  /* DATA FETCHING                                                    */
  /* ================================================================ */

  const loadPayouts = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        setError(null);
        const params = { page, per_page: PER_PAGE };
        if (statusFilter !== "all") params.status = statusFilter;

        const res = await api.get("/admin/payouts", { params });
        if (res.data.success) {
          setPayouts(res.data.data.data || []);
          setPagination({
            current_page: res.data.data.current_page || 1,
            last_page: res.data.data.last_page || 1,
            total: res.data.data.total || 0,
          });
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load payouts");
      } finally {
        setLoading(false);
      }
    },
    [statusFilter],
  );

  useEffect(() => {
    loadPayouts(1);
  }, [loadPayouts]);

  /* ================================================================ */
  /* OPEN PAYOUT DIALOG (with bic)                                    */
  /* ================================================================ */

  const openPayoutDialog = useCallback(async (operatorId, operatorName) => {
    setPayoutDialogOpen(true);
    setWalletLoading(true);
    setWalletData(null);
    setPayoutForm(EMPTY_PAYOUT_FORM);

    try {
      const res = await api.get(
        `/admin/payouts/operators/${operatorId}/wallet`,
      );
      if (res.data.success) {
        const d = res.data.data;
        setWalletData(d);
        setSelectedOperator({
          id: operatorId,
          name: operatorName || d.operator_name,
        });
        setPayoutForm({
          amount: d.available > 0 ? Number(d.available).toFixed(2) : "",
          method: d.payout_method || "gcash",
          account_name: d.payout_account_name || "",
          account_number: d.payout_account_number || "",
          bank_code: d.payout_bank_code || "",
          bic: d.payout_bic || "", // ← auto-fill BIC if operator has it
        });
      } else {
        throw new Error(res.data.message || "Failed to load wallet");
      }
    } catch (err) {
      MySwal.fire(
        "Error",
        err.response?.data?.message || "Failed to load wallet",
        "error",
      );
      setPayoutDialogOpen(false);
    } finally {
      setWalletLoading(false);
    }
  }, []);

  /* Auto-open payout dialog when ?operator=<id> is present.
     Uses a ref so this runs only once (not on every render). */
  useEffect(() => {
    if (autoOpenedRef.current) return;
    const opId = searchParams.get("operator");
    if (opId) {
      autoOpenedRef.current = true;
      openPayoutDialog(opId);
    }
  }, [searchParams, openPayoutDialog]);

  /* ================================================================ */
  /* SUBMIT PAYOUT (with bic validation + failed response handling)   */
  /* ================================================================ */

  const submitPayout = async () => {
    /* Validation */
    if (!payoutForm.amount || parseFloat(payoutForm.amount) <= 0) {
      MySwal.fire("Invalid Amount", "Please enter a valid amount", "warning");
      return;
    }
    if (!payoutForm.account_name || !payoutForm.account_number) {
      MySwal.fire(
        "Missing Info",
        "Account name and number are required",
        "warning",
      );
      return;
    }
    if (payoutForm.method === "bank" && !payoutForm.bank_code) {
      MySwal.fire(
        "Missing Bank Code",
        "Bank code is required for bank transfers",
        "warning",
      );
      return;
    }
    if (payoutForm.method === "bank" && !payoutForm.bic) {
      MySwal.fire(
        "Missing BIC",
        "Bank BIC / SWIFT code is required for bank transfers (e.g. BNORPHMM for BDO).",
        "warning",
      );
      return;
    }

    const confirm = await MySwal.fire({
      title: "Confirm Payout",
      html: `Send <b>${formatCurrency(payoutForm.amount)}</b> to <b>${payoutForm.account_name}</b> via <b>${payoutForm.method.toUpperCase()}</b>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, send payout",
      confirmButtonColor: Colors.PRIMARY,
    });
    if (!confirm.isConfirmed) return;

    try {
      setSubmitting(true);
      const res = await api.post(
        `/admin/payouts/operators/${selectedOperator.id}`,
        payoutForm,
      );

      const payout = res.data?.data;
      const payoutStatus = payout?.status;

      setPayoutDialogOpen(false);
      setSelectedOperator(null);
      setWalletData(null);

      if (payoutStatus === "failed") {
        MySwal.fire({
          icon: "error",
          title: "Payout Failed",
          html: `<div style="text-align:left; font-size:14px;">
            <p style="margin-bottom:10px;">
              <strong>Reason:</strong> ${
                payout?.failure_reason ||
                res.data?.message ||
                "PayMongo rejected the transfer."
              }
            </p>
            <p style="color:#6b7280; font-size:13px; margin:0;">
              The operator has been notified and the wallet has been refunded.
            </p>
          </div>`,
          confirmButtonColor: Colors.PRIMARY,
        });
      } else if (payoutStatus === "succeeded") {
        MySwal.fire({
          icon: "success",
          title: "Payout Sent!",
          text: res.data.message || "Payout completed successfully.",
          timer: 2500,
          timerProgressBar: true,
        });
      } else {
        MySwal.fire({
          icon: "info",
          title: "Payout Processing",
          text: res.data.message || `Status: ${payoutStatus || "pending"}.`,
        });
      }

      loadPayouts(1);
    } catch (err) {
      MySwal.fire(
        "Payout Failed",
        err.response?.data?.message || "Failed to send payout.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ================================================================ */
  /* SYNC PAYOUT STATUS (Force PayMongo Check)                        */
  /* ================================================================ */

  const handleSyncPayout = async (payoutId) => {
    try {
      MySwal.fire({
        title: "Syncing with PayMongo...",
        allowOutsideClick: false,
        didOpen: () => {
          MySwal.showLoading();
        },
      });

      // Call the new backend route we added
      const res = await api.post(`/admin/payouts/${payoutId}/sync`);

      MySwal.close();

      if (res.data.success) {
        MySwal.fire({
          icon: "success",
          title: "Sync Successful",
          text: res.data.message,
          timer: 2000,
          showConfirmButton: false,
        });
        // Refresh the table
        loadPayouts(pagination.current_page);
      } else {
        MySwal.fire("Notice", res.data.message, "info");
      }
    } catch (err) {
      MySwal.close();
      MySwal.fire(
        "Sync Failed",
        err.response?.data?.message || "Failed to sync with PayMongo",
        "error",
      );
    }
  };

  /* ================================================================ */
  /* CLOSE DIALOG                                                     */
  /* ================================================================ */

  const closePayoutDialog = () => {
    if (submitting) return;
    setPayoutDialogOpen(false);
    setSelectedOperator(null);
    setWalletData(null);
    setPayoutForm(EMPTY_PAYOUT_FORM);
    // If we auto-opened from ?operator=..., clear it from the URL
    if (searchParams.get("operator")) {
      navigate("/payouts", { replace: true });
    }
  };

  /* ================================================================ */
  /* STATS                                                            */
  /* ================================================================ */

  const pageStats = {
    succeeded: payouts.filter(
      (p) => (p.status || "").toLowerCase() === "succeeded",
    ).length,
    processing: payouts.filter(
      (p) => (p.status || "").toLowerCase() === "processing",
    ).length,
    pending: payouts.filter((p) => (p.status || "").toLowerCase() === "pending")
      .length,
    failed: payouts.filter((p) => (p.status || "").toLowerCase() === "failed")
      .length,
    totalAmount: payouts.reduce(
      (sum, p) => sum + (parseFloat(p.amount) || 0),
      0,
    ),
  };

  const statCards = [
    {
      k: "Succeeded (page)",
      v: pageStats.succeeded,
      i: "bi-check-circle",
      c: Colors.SUCCESS,
    },
    {
      k: "Processing (page)",
      v: pageStats.processing,
      i: "bi-hourglass-split",
      c: Colors.WARNING,
    },
    {
      k: "Pending (page)",
      v: pageStats.pending,
      i: "bi-clock-history",
      c: Colors.WARNING,
    },
    {
      k: "Failed (page)",
      v: pageStats.failed,
      i: "bi-x-circle",
      c: Colors.RED,
    },
    {
      k: "Total Payouts",
      v: pagination.total,
      i: "bi-cash-stack",
      c: Colors.PRIMARY,
    },
    {
      k: "Page Volume",
      v: formatCurrency(pageStats.totalAmount),
      i: "bi-currency-exchange",
      c: Colors.INFO,
      isText: true,
    },
  ];

  /* ================================================================ */
  /* PAGINATION                                                       */
  /* ================================================================ */

  const renderPaginationItems = () => {
    const items = [];
    const max = 5;
    const current = pagination.current_page;
    const totalPages = pagination.last_page;
    let start = Math.max(1, current - Math.floor(max / 2));
    const end = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    for (let n = start; n <= end; n++) {
      items.push(
        <Pagination.Item
          key={n}
          active={n === current}
          onClick={() => loadPayouts(n)}
        >
          {n}
        </Pagination.Item>,
      );
    }
    return items;
  };

  /* ================================================================ */
  /* RENDER                                                           */
  /* ================================================================ */

  return (
    <div className="bk-page">
      <Styles />
      <Container fluid>
        <div className="bk-header">
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h2>Operator Payouts</h2>
            <p>Send earnings to operators via PayMongo</p>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </Alert>
        )}

        <Row className="g-3 mb-4">
          {statCards.map((s) => (
            <Col key={s.k} xs={6} md={4} lg={2}>
              <div className="bk-stat">
                <div
                  className="bk-stat-icon"
                  style={{ background: `${s.c}1f`, color: s.c }}
                >
                  <i className={`bi ${s.i}`} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    className={`bk-stat-val${s.isText ? " text" : ""}`}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.v}
                  </div>
                  <div className="bk-stat-lbl">{s.k}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div className="bk-toolbar">
          <Row className="g-2">
            <Col md={8}>
              <Dropdown>
                <Dropdown.Toggle className="bk-filter-btn w-100">
                  <i className="bi bi-funnel me-2" />
                  {statusFilter === "all"
                    ? "All Status"
                    : getStatusMeta(statusFilter).label}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {STATUS_OPTIONS.map((o) => (
                    <Dropdown.Item
                      key={o.value}
                      active={statusFilter === o.value}
                      onClick={() => setStatusFilter(o.value)}
                    >
                      {o.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col md={4}>
              <Button
                className="bk-btn-ghost w-100"
                onClick={() => loadPayouts(1)}
                disabled={loading}
                style={{ height: 42 }}
              >
                <i
                  className={`bi bi-arrow-clockwise me-2 ${loading ? "spin" : ""}`}
                />
                Refresh
              </Button>
            </Col>
          </Row>
        </div>

        <div className="bk-card">
          <div className="bk-card-header">
            <h5>
              <i
                className="bi bi-cash-stack"
                style={{ color: Colors.PRIMARY }}
              />
              Payout History
            </h5>
            <small>
              {pagination.total > 0
                ? `Page ${pagination.current_page} of ${pagination.last_page} · ${pagination.total} total`
                : "No records"}
            </small>
          </div>

          {loading ? (
            <div style={{ padding: 20 }}>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bk-skel mb-3" style={{ height: 42 }} />
              ))}
            </div>
          ) : payouts.length === 0 ? (
            <div className="bk-empty">
              <div className="bk-empty-icon">
                <i className="bi bi-cash-stack" />
              </div>
              <h5 style={{ color: Colors.TEXT_DARK }}>No payouts yet</h5>
              <p className="text-muted mb-0">
                {statusFilter !== "all"
                  ? "No payouts match the current filter."
                  : "Payouts sent to operators will appear here."}
              </p>
            </div>
          ) : (
            <>
              <Table responsive hover className="bk-table">
                <thead>
                  <tr>
                    <th>Payout</th>
                    <th>Operator</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th>Method</th>
                    <th>Account</th>
                    <th>Status</th>
                    <th>Reference</th>
                    <th>Date</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.payout_id}>
                      <td>
                        <span className="bk-id-chip">#{p.payout_id}</span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="bk-avatar">
                            {getInitials(p.operator, p.operator_id)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                color: Colors.TEXT_DARK,
                              }}
                            >
                              {getOperatorName(p)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontWeight: 700,
                          color: Colors.TEXT_DARK,
                        }}
                      >
                        {formatCurrency(p.amount)}
                      </td>
                      <td>
                        <MethodPill method={p.method} />
                      </td>
                      <td>
                        <div
                          style={{ fontWeight: 500, color: Colors.TEXT_DARK }}
                        >
                          {p.account_name || "—"}
                        </div>
                        <small style={{ color: Colors.TEXT_LIGHT }}>
                          {p.account_number || "—"}
                        </small>
                      </td>
                      <td>
                        <StatusPill status={p.status} />
                      </td>
                      <td>
                        <span
                          style={{
                            fontFamily: "ui-monospace, monospace",
                            fontSize: 12,
                            color: Colors.TEXT_LIGHT,
                          }}
                        >
                          {p.reference_number || "—"}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{ color: Colors.TEXT_LIGHT, fontSize: 13 }}
                        >
                          {formatDateTime(p.created_at)}
                        </span>
                      </td>
                                            {/* 👇 ADD THIS NEW COLUMN */}
                      <td style={{ textAlign: "right" }}>
                        {(p.status === "processing" || p.status === "pending") && (
                          <Button
                            size="sm"
                            className="bk-btn-ghost"
                            onClick={() => handleSyncPayout(p.payout_id)}
                            title="Ask PayMongo for the real status"
                          >
                            <i className="bi bi-arrow-repeat me-1" />
                            Sync
                          </Button>
                        )}
                        {p.status === "succeeded" && (
                          <span className="text-success" style={{ fontSize: 13, fontWeight: 600 }}>
                            <i className="bi bi-check-circle me-1" /> Done
                          </span>
                        )}
                        {p.status === "failed" && (
                          <span className="text-danger" style={{ fontSize: 13, fontWeight: 600 }}>
                            <i className="bi bi-x-circle me-1" /> Failed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {pagination.last_page > 1 && (
                <div
                  className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                  style={{ padding: "14px 20px", borderTop: T.border }}
                >
                  <small className="text-muted">
                    Page {pagination.current_page} of {pagination.last_page}
                  </small>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      disabled={pagination.current_page === 1}
                      onClick={() => loadPayouts(pagination.current_page - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={
                        pagination.current_page === pagination.last_page
                      }
                      onClick={() => loadPayouts(pagination.current_page + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>

        {/* ============ PAYOUT DIALOG ============ */}
        <Modal
          show={payoutDialogOpen}
          onHide={closePayoutDialog}
          size="lg"
          centered
        >
          <Modal.Header closeButton={!submitting}>
            <Modal.Title>
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: `${Colors.SUCCESS}1f`,
                    color: Colors.SUCCESS,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <i className="bi bi-cash-stack" />
                </span>
                Send Payout — {selectedOperator?.name || "Operator"}
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {walletLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted mb-0">Loading wallet…</p>
              </div>
            ) : walletData ? (
              <>
                {/* Wallet summary */}
                <div className="bk-section">
                  <div className="bk-section-title">
                    <i className="bi bi-wallet2" /> Wallet Summary
                  </div>

                  {/* Primary figures */}
                  <Row className="g-3 mb-3">
                    <Col md={3}>
                      <small className="text-muted d-block">
                        Wallet Balance
                      </small>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: Colors.SUCCESS,
                        }}
                      >
                        {formatCurrency(
                          walletData.wallet_balance || walletData.available,
                        )}
                      </div>
                      <small className="text-muted">From GCash bookings</small>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted d-block">
                        Cash Commission Owed
                      </small>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: Colors.WARNING,
                        }}
                      >
                        {formatCurrency(walletData.cash_commission_owed || 0)}
                      </div>
                      <small className="text-muted">To be collected</small>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted d-block">
                        Net Payout Available
                      </small>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: Colors.PRIMARY,
                        }}
                      >
                        {formatCurrency(
                          walletData.net_payout_available ||
                            walletData.available,
                        )}
                      </div>
                      <small className="text-muted">After deduction</small>
                    </Col>
                    <Col md={3}>
                      <small className="text-muted d-block">
                        Pending Payouts
                      </small>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: Colors.TEXT_DARK,
                        }}
                      >
                        {formatCurrency(walletData.pending_payouts || 0)}
                      </div>
                    </Col>
                  </Row>

                  {/* Earnings breakdown */}
                  <div
                    style={{
                      background: "#fff",
                      border: `1px solid ${Colors.BORDER_GRAY}`,
                      borderRadius: 10,
                      padding: 14,
                      marginTop: 12,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        color: Colors.TEXT_LIGHT,
                        marginBottom: 10,
                      }}
                    >
                      <i className="bi bi-graph-up me-1" /> Lifetime Earnings
                      Breakdown
                    </div>
                    <Row className="g-3">
                      <Col md={4}>
                        <small className="text-muted d-block">
                          GCash Bookings
                        </small>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: Colors.SUCCESS,
                          }}
                        >
                          {formatCurrency(walletData.gcash_earnings || 0)}
                        </div>
                        <small className="text-muted">Credited to wallet</small>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">
                          Cash Bookings
                        </small>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: Colors.INFO,
                          }}
                        >
                          {formatCurrency(walletData.cash_earnings || 0)}
                        </div>
                        <small className="text-muted">
                          Received in cash directly
                        </small>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">
                          Total Earnings
                        </small>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: Colors.TEXT_DARK,
                          }}
                        >
                          {formatCurrency(walletData.total_earnings || 0)}
                        </div>
                        <small className="text-muted">
                          All payment methods
                        </small>
                      </Col>
                    </Row>
                  </div>

                  {/* Cash commission notice */}
                  {walletData.cash_commission_owed > 0 && (
                    <Alert variant="warning" className="mt-3 mb-0">
                      <i className="bi bi-info-circle me-2" />
                      This operator owes{" "}
                      <strong>
                        {formatCurrency(walletData.cash_commission_owed)}
                      </strong>{" "}
                      in cash commission. It will be automatically deducted from
                      this payout — the operator will receive{" "}
                      <strong>
                        {formatCurrency(
                          Math.max(
                            0,
                            (parseFloat(payoutForm.amount) || 0) -
                              walletData.cash_commission_owed,
                          ),
                        )}
                      </strong>
                      .
                    </Alert>
                  )}
                </div>

                {walletData.available <= 0 && (
                  <Alert variant="warning">
                    <i className="bi bi-exclamation-triangle me-2" />
                    This operator has no available balance to pay out.
                  </Alert>
                )}

                {/* Payout form */}
                <Form>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 13, fontWeight: 500 }}>
                          Amount <span style={{ color: Colors.RED }}>*</span>
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text>₱</InputGroup.Text>
                          <FormControl
                            type="number"
                            step="0.01"
                            min="0"
                            max={walletData.available}
                            placeholder="0.00"
                            value={payoutForm.amount}
                            onChange={(e) =>
                              setPayoutForm({
                                ...payoutForm,
                                amount: e.target.value,
                              })
                            }
                            disabled={walletData.available <= 0}
                          />
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Max:{" "}
                          {formatCurrency(
                            walletData.wallet_balance || walletData.available,
                          )}
                          {walletData.cash_commission_owed > 0 && (
                            <>
                              {" "}
                              · Net to operator:{" "}
                              {formatCurrency(
                                Math.max(
                                  0,
                                  (parseFloat(payoutForm.amount) || 0) -
                                    walletData.cash_commission_owed,
                                ),
                              )}
                            </>
                          )}
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 13, fontWeight: 500 }}>
                          Payout Method
                        </Form.Label>
                        <Form.Select
                          value={payoutForm.method}
                          onChange={(e) =>
                            setPayoutForm({
                              ...payoutForm,
                              method: e.target.value,
                            })
                          }
                          style={{ borderRadius: T.radiusSm }}
                        >
                          <option value="gcash">GCash</option>
                          <option value="bank">Bank Transfer</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 13, fontWeight: 500 }}>
                          Account Name{" "}
                          <span style={{ color: Colors.RED }}>*</span>
                        </Form.Label>
                        <FormControl
                          type="text"
                          placeholder="e.g., Juan Dela Cruz"
                          value={payoutForm.account_name}
                          onChange={(e) =>
                            setPayoutForm({
                              ...payoutForm,
                              account_name: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 13, fontWeight: 500 }}>
                          {payoutForm.method === "gcash"
                            ? "GCash Number"
                            : "Bank Account Number"}{" "}
                          <span style={{ color: Colors.RED }}>*</span>
                        </Form.Label>
                        <FormControl
                          type="text"
                          placeholder={
                            payoutForm.method === "gcash"
                              ? "09171234567"
                              : "1234567890"
                          }
                          value={payoutForm.account_number}
                          onChange={(e) =>
                            setPayoutForm({
                              ...payoutForm,
                              account_number: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    {payoutForm.method === "bank" && (
                      <>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label
                              style={{ fontSize: 13, fontWeight: 500 }}
                            >
                              Bank Code{" "}
                              <span style={{ color: Colors.RED }}>*</span>
                            </Form.Label>
                            <FormControl
                              type="text"
                              placeholder="BDO, BPI, METROBANK…"
                              value={payoutForm.bank_code}
                              onChange={(e) =>
                                setPayoutForm({
                                  ...payoutForm,
                                  bank_code: e.target.value.toUpperCase(),
                                })
                              }
                            />
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group>
                            <Form.Label
                              style={{ fontSize: 13, fontWeight: 500 }}
                            >
                              Bank BIC / SWIFT{" "}
                              <span style={{ color: Colors.RED }}>*</span>
                            </Form.Label>
                            <FormControl
                              type="text"
                              list="common-bics"
                              placeholder="BNORPHMM"
                              value={payoutForm.bic}
                              onChange={(e) =>
                                setPayoutForm({
                                  ...payoutForm,
                                  bic: e.target.value.toUpperCase(),
                                })
                              }
                            />
                            <datalist id="common-bics">
                              {COMMON_BICS.map((b) => (
                                <option key={b.code} value={b.code}>
                                  {b.label} ({b.code})
                                </option>
                              ))}
                            </datalist>
                            <Form.Text className="text-muted">
                              Required by PayMongo. Common: BDO → BNORPHMM, BPI
                              → BOPIPHMM, Metrobank → MBTCPHMM.
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </>
                    )}
                  </Row>

                  <Alert variant="info" className="mt-3 mb-0 small">
                    <i className="bi bi-info-circle me-2" />
                    Payouts via InstaPay settle within a few minutes. A ₱10
                    transfer fee may apply.
                  </Alert>
                </Form>
              </>
            ) : (
              <Alert variant="danger">Failed to load wallet data.</Alert>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              className="bk-btn-ghost"
              onClick={closePayoutDialog}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              className="bk-btn-primary"
              onClick={submitPayout}
              disabled={
                submitting ||
                walletLoading ||
                !walletData ||
                walletData.available <= 0
              }
              style={{ background: Colors.SUCCESS }}
            >
              {submitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Sending…
                </>
              ) : (
                <>
                  <i className="bi bi-send me-1" />
                  Send Payout
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};;

export default Payouts;